import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, useTheme, useMediaQuery, LinearProgress, Tooltip, Avatar, Modal } from '@mui/material';
import React from 'react';
import { League, LeagueGame, Player, PlayerGameScore, RosterPositionEnum, NflGame } from '../../../sdk/model';
import { positionOrderNoBench } from '../../util/PositionOrder';
import { useAppSelector } from '../../../app/hooks';
import { Box } from '@mui/system';
import { getTeamFromLeague } from '../../util/TeamUtils';
import './style.scss';
import { usePlayerDataLoadFlat } from '../../util/PlayerDataUtil';
import { ScoreToolTip } from '../../util/GameUtil';
import PlayerCardLink from '../../cards/playerCardLink';
import { Properties } from '../../../Properties';
import { useUpdatePlayerDataFromMessage, useUpdateNflGameDataFromMessage } from '../../../app/dataLoaderHooks';
import useWebSocketListener from '../../web-sockets/WebSocketListener';
import playerMatchupDisplay from '../../util/PlayerMatchupDisplay';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayerInfoCard from '../../cards/playerCard';

const emptyPosition = "Empty";

const COMPLETED_GAME_STATUSES = new Set(['COMPLETE', 'CLOSED']);
const INACTIVE_ROSTER_POSITIONS = new Set(['BN', 'IR']);

// Helper function to get consistent matchup display text (no multi-line Box components)
function getMatchupDisplayText(currentWeekStats: any, nflGame: NflGame | undefined, player: Player | undefined): string {
  if (!nflGame) {
    return "BYE";
  }
  
  const game = nflGame as any; // Type assertion for missing properties
  const opponentString = player ? 
    game.awayTeamAlias === player.nfl_team?.alias ? "@" + game.homeTeamAlias : game.awayTeamAlias
    : (currentWeekStats?.isHome ? "" : "@") + currentWeekStats?.opponent;
  
  if (nflGame && (nflGame.quarter || COMPLETED_GAME_STATUSES.has(nflGame.status!))) {
    // For active/completed games, show opponent and score in a single line
    const gameTime = nflGame.status === 'COMPLETE' || nflGame.status === 'CLOSED' ? 'Final' : 
                    nflGame.clock === '00:00' && nflGame.quarter === 2 ? 'HALF' :
                    nflGame.clock === '00:00' && nflGame.quarter === 4 ? 'End' :
                    nflGame.quarter! > 4 ? 'OT' : `Q${nflGame.quarter}`;
    return `${opponentString} ${gameTime} ${nflGame.awayPts}-${nflGame.homePts}`;
  }
  
  // For future games, show opponent and time
  const gametime = new Date(currentWeekStats?.startDateTime!);
  return opponentString + " " + gametime.toLocaleDateString(undefined, { weekday: 'short' }) + " " + gametime.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'});
}

// Helper function to get game time info
function getGameTimeInfo(nflGame: NflGame | undefined) {
  if (!nflGame) return null;
  
  const isLive = !!nflGame.quarter;
  const isCompleted = COMPLETED_GAME_STATUSES.has(nflGame.status!);
  
  if (isCompleted) {
    return { status: 'FINAL', color: '#9e9e9e', icon: null };
  } else if (isLive) {
    const quarter = nflGame.quarter;
    const clock = nflGame.clock || '';
    return { 
      status: `Q${quarter} ${clock}`, 
      color: '#4caf50', 
      icon: PlayArrowIcon,
      isLive: true 
    };
  } else {
    // Game hasn't started yet
    return { status: 'SCHEDULED', color: '#2196f3', icon: AccessTimeIcon };
  }
}

interface MatchStartersProps {
  league: League,
  game: LeagueGame
}


function MatchupStarters(props: MatchStartersProps) {
  const [game, setGame] = React.useState<LeagueGame>({...props.game}); 
  React.useEffect(() => {
    setGame({...props.game});
  }, [props.game]);
  const playerMap = usePlayerDataLoadFlat() as Map<string | undefined, Player | undefined>;
  const playerStatsWebSocketUrl = Properties.websocketsUrl + '/ws/playerStats/genericUser/genericKey';
  useWebSocketListener(playerStatsWebSocketUrl, useUpdatePlayerDataFromMessage, []);
  const nflGames = useAppSelector((state) => state.nflGameDataLoad );
  const nflGameWebSocketUrl = Properties.websocketsUrl + '/ws/nflGames/genericUser/' + game.seasonYear + '-' + game.weekNumber;
  useWebSocketListener(nflGameWebSocketUrl, useUpdateNflGameDataFromMessage, []);
  const nflGameMap = new Map(nflGames.flatMap(nflGame => {
    const game = nflGame as any; // Type assertion since model is missing team alias properties
    return [[game.awayTeamAlias, nflGame], [game.homeTeamAlias, nflGame]];
  })) as Map<string, NflGame>;
  // This spreads confetti on the player name that won (in theory)
  const rosterSettings: { [key: string]: number; } | undefined = props.league.rosterSettings;
  const currentWeek = props.league.currentSeason?.currentWeek === game.weekNumber;
  const starters = positionOrderNoBench.map((position) => {
    return (
      getRowsForPosition(position, rosterSettings, game, playerMap, props.league.id, nflGameMap, currentWeek)
    );
  })

  const bench = getRowsForPosition(RosterPositionEnum.Bn , rosterSettings, game, playerMap, props.league.id, nflGameMap, currentWeek);
  const irPlayers = getRowsForPosition(RosterPositionEnum.Ir , rosterSettings, game, playerMap, props.league.id, nflGameMap, currentWeek);

  return (
    <Box>
      <TableContainer className='starters-table'>
        <Table className="player-info-table">
          <TableHead>
            <TableRow>
              <TableCell className="team-row" key="away-header">{getHeaderCell(game, props.league, true, playerMap, nflGameMap, currentWeek)}</TableCell>
              <TableCell className="position-row" key="vs-header">{getVsHeaderCell(game, props.league)}</TableCell>
              <TableCell className="team-row" key="home-header">{getHeaderCell(game, props.league, false, playerMap, nflGameMap, currentWeek)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getStatersHeaderRow(game, props.league)}
            {starters}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer className='bench-table'>
        <Table className="player-info-table">
          <TableHead>
            <TableRow>
              <TableCell className="team-row" key="home-header"></TableCell>
              <TableCell className="position-row" key="spacer-header" ></TableCell>
              <TableCell  className="team-row" key="away-header"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getBenchHeaderRow(game, props.league)}
            {bench}
          </TableBody>
        </Table>
      </TableContainer>
      {Object.keys(props.league.rosterSettings!).includes(RosterPositionEnum.Ir) &&
        <TableContainer className='ir-table'>
          <Table className="player-info-table">
            <TableHead>
              <TableRow>
                <TableCell className="team-row" key="home-header"></TableCell>
                <TableCell className="position-row" key="spacer-header" ></TableCell>
                <TableCell  className="team-row" key="away-header"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getIrHeaderRow(game, props.league)}
              {irPlayers}
            </TableBody>
          </Table>
        </TableContainer>
      }
      
    </Box>
  );
}

const TeamHeader = React.memo(({ 
  game, 
  league, 
  away, 
  playerMap, 
  nflGameMap, 
  currentWeek 
}: { 
  game: LeagueGame; 
  league: League; 
  away: boolean; 
  playerMap: Map<string | undefined, Player | undefined>; 
  nflGameMap: Map<string, NflGame>; 
  currentWeek: boolean; 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const teamId = away ? game.awayTeamId : game.homeTeamId;
  const score = away ? game.awayTeamScore : game.homeTeamScore;
  const projection = (away ? game.awayTeamProjection : game.homeTeamProjection) ?? 0;
  const team = getTeamFromLeague(league, teamId);

  const isWinner = game.winningTeamId === teamId && game.finalScore;
  const isLoser = game.finalScore && !isWinner;
  
  // Calculate player status counts
  const playerCounts = React.useMemo(() => {
    if (!team?.playerRoster) return { playersInPlay: 0, playersRemaining: 0, playersCompleted: 0 };
    
    return Object.keys(team.playerRoster)
      .filter(rosterPos => !INACTIVE_ROSTER_POSITIONS.has(rosterPos))
      .flatMap(rosterPos => team.playerRoster![rosterPos])
      .reduce((reducer, rosterSpot) => {
        const playerInfo = playerMap.get(rosterSpot?.playerId);
        const currentWeekStats = playerInfo?.quick_stats?.currentWeeksStats;
        const nflGame = currentWeekStats ? nflGameMap.get(playerInfo.nfl_team?.alias!) : undefined;
        
        if (nflGame?.quarter && !COMPLETED_GAME_STATUSES.has(nflGame.status!)) {
          reducer.playersInPlay++;
        } else if (nflGame && !COMPLETED_GAME_STATUSES.has(nflGame.status!)) {
          reducer.playersRemaining++;
        } else if (nflGame && COMPLETED_GAME_STATUSES.has(nflGame.status!)) {
          reducer.playersCompleted++;
        }
        return reducer;
      }, { playersInPlay: 0, playersRemaining: 0, playersCompleted: 0 });
  }, [team, playerMap, nflGameMap]);
  
  const adjustment = away ? game.awayTeamScoreAdjustment : game.homeTeamScoreAdjustment;

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      p: { xs: 1, sm: 2 },
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {/* Team Name */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        mb: 1,
        minHeight: { xs: '28px', sm: 'auto' }
      }}>
        <Typography 
          variant="h5" 
          fontWeight="bold" 
          sx={{ 
            color: isWinner ? 'success.main' : isLoser ? 'error.main' : 'text.primary',
            fontSize: { xs: '1rem', sm: '1.25rem' },
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: { xs: '160px', sm: '220px' }
          }}
        >
          {team?.name}
        </Typography>
      </Box>

      {/* Score Display - Made Smaller */}
      <Box sx={{ mb: currentWeek ? 1 : 0 }}>
        <Typography 
          variant="h4" 
          fontWeight="bold" 
          sx={{ 
            color: isWinner ? 'success.main' : isLoser ? 'error.main' : 'text.primary',
            fontSize: { xs: '1.5rem', sm: '2rem' },
            lineHeight: 1,
            mb: 0.5,
          }}
        >
          {score ?? 0}
        </Typography>
        {adjustment !== 0 && (
          <Box>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontStyle: 'italic', 
              fontSize: { xs: '0.7rem', sm: '0.8rem' }
            }}
          >
            Adj: {adjustment > 0 ? '+' + adjustment : adjustment}
          </Typography>
          </Box>
        )}
        {currentWeek && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontStyle: 'italic', 
              fontSize: { xs: '0.7rem', sm: '0.8rem' }
            }}
          >
            Proj: {projection.toFixed(1)}
          </Typography>
        )}
      </Box>

      {/* Live Game Status */}
      {currentWeek && (playerCounts.playersInPlay > 0 || playerCounts.playersRemaining > 0) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: game.finalScore ? 1 : 0 }}>
          {playerCounts.playersInPlay > 0 && (
            <Chip
              icon={<PlayArrowIcon sx={{ fontSize: '0.6rem !important' }} />}
              label={`${playerCounts.playersInPlay} Live`}
              size="small"
              sx={{
                height: { xs: 16, sm: 20 },
                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                color: '#4caf50',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                border: '1px solid rgba(76, 175, 80, 0.3)',
              }}
            />
          )}
          {playerCounts.playersRemaining > 0 && (
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: '0.6rem !important' }} />}
              label={`${playerCounts.playersRemaining} Pending`}
              size="small"
              sx={{
                height: { xs: 16, sm: 20 },
                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                color: '#2196f3',
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                border: '1px solid rgba(33, 150, 243, 0.3)',
              }}
            />
          )}
        </Box>
      )}

      {/* Winner/Loser Badge - At Bottom */}
      {game.finalScore && (
        <Chip
          label={isWinner ? 'Winner' : 'Loser'}
          size="small"
          color={isWinner ? 'success' : 'error'}
          sx={{
            fontWeight: 'bold',
            fontSize: { xs: '0.55rem', sm: '0.7rem' },
            height: { xs: '18px', sm: '22px' },
            ...(isWinner && {
              background: 'linear-gradient(45deg, #66bb6a, #4caf50)',
              color: 'white',
            })
          }}
        />
      )}
    </Box>
  );
});

function getHeaderCell(game: LeagueGame, league: League, away: boolean, playerMap: Map<string | undefined, Player | undefined>, nflGameMap: Map<string, NflGame>, currentWeek: boolean) {
  return (
    <TeamHeader
      game={game}
      league={league}
      away={away}
      playerMap={playerMap}
      nflGameMap={nflGameMap}
      currentWeek={currentWeek}
    />
  );
}

function getVsHeaderCell(game: LeagueGame, league: League) {
  const currentWeek = league.currentSeason?.currentWeek;
  const isLive = currentWeek === game.weekNumber;
  const isCompleted = game.finalScore;
  
  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      p: { xs: 1, sm: 2 },
      minHeight: { xs: '120px', sm: '140px' }
    }}>
      {/* Fun VS */}
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontWeight: 'bold',
          color: isLive ? 'success.main' : isCompleted ? 'text.secondary' : 'primary.main',
          background: isLive ? 
            'linear-gradient(45deg, #4caf50, #66bb6a)' : 
            isCompleted ? 
            'linear-gradient(45deg, #9e9e9e, #bdbdbd)' :
            'linear-gradient(45deg, #2196f3, #64b5f6)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textShadow: '0 1px 2px rgba(0,0,0,0.1)',
          mb: 1
        }}
      >
        VS
      </Typography>
      
      {/* Week indicator */}
      <Typography
        variant="caption"
        sx={{
          fontSize: { xs: '0.6rem', sm: '0.7rem' },
          color: 'text.secondary',
          fontWeight: 'medium'
        }}
      >
        Week {game.weekNumber}
      </Typography>
      
      {/* Season year */}
      <Typography
        variant="caption"
        sx={{
          fontSize: { xs: '0.55rem', sm: '0.65rem' },
          color: 'text.secondary'
        }}
      >
        {game.seasonYear}
      </Typography>
    </Box>
  );
}

function getStatersHeaderRow(game: LeagueGame, league: League) {
  let gameState = "Not Started";
  let stateClass = "not-started";
  
  const currentWeek = league.currentSeason?.currentWeek;
  if (currentWeek !== undefined && game.weekNumber !== undefined) {
    if (currentWeek > game.weekNumber || game.finalScore) {
      gameState = "Final";
      stateClass = "final";
    } else if (currentWeek === game.weekNumber) {
      gameState = "In Progress";
      stateClass = "in-progress";
    }
  }
    return (
    <TableRow className='starters head-row'>
      <TableCell><Box className='head-text'>Starters</Box></TableCell>
      <TableCell></TableCell>
    <TableCell>
      <Box className={'gameState'} sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 0.5, sm: 1 },
        textAlign: { xs: 'left', sm: 'right' }
      }}>
        <Box component="span" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>Game State:</Box>
        <Box component="span" className={`game-state ${stateClass}`} sx={{ 
          fontSize: { xs: '0.75rem', sm: '0.9rem' },
          whiteSpace: 'nowrap'
        }}>{gameState}</Box>
      </Box>
    </TableCell>
  </TableRow>
  );
}


function getBenchHeaderRow(game: LeagueGame, league: League) {
  return (
  <TableRow className='bench head-row' key="bench">
    <TableCell colSpan={3}><Box className='head-text'>Bench</Box></TableCell>
  </TableRow>
  );
}

function getIrHeaderRow(game: LeagueGame, league: League) {
  return (
  <TableRow className='bench head-row' key="bench">
    <TableCell colSpan={3}><Box className='head-text'>Injured Reserve (IR)</Box></TableCell>
  </TableRow>
  );
}

function getRowsForPosition(position: string,
  rosterSettings: { [key: string]: number; } | undefined,
  game: LeagueGame,
  playerMap: Map<string | undefined, Player | undefined>,
  leagueId: string | undefined, nflGameMap: Map<string, NflGame>, currentWeek: boolean) {
  if (rosterSettings === null || rosterSettings === undefined) {
    return (
      <React.Fragment></React.Fragment>
    );
  }

  const numTimes = rosterSettings[position] || 0;

  return (
    <React.Fragment>
      {Array.from({ length: numTimes }).map((_, i) => (
        <TableRow key={`${position}-${i}`}>
          {/* Render table cells for each position */}
          <TableCell>{getPlayerFromRoster(game?.awayTeamRoster, playerMap, leagueId, position, i, true, nflGameMap, currentWeek)}</TableCell>
          <TableCell><Box className="score-position">{position}</Box></TableCell>
          <TableCell>{getPlayerFromRoster(game?.homeTeamRoster, playerMap, leagueId, position, i, false, nflGameMap, currentWeek)}</TableCell>
        </TableRow>
      ))}
    </React.Fragment>
  );
}

// Helper function to determine score color based on game status and performance
function getScoreColor(
  projection: number,
  originalProjection: number,
  nflGame: NflGame | undefined,
  theme: any,
  currentWeek: boolean
) {
  if (!currentWeek) {
    return theme.palette.text.secondary;
  }
  const isLive = !!nflGame?.quarter;
  const isCompleted = COMPLETED_GAME_STATUSES.has(nflGame?.status!);
  if (!isLive && !isCompleted) {
    return theme.palette.text.secondary;
  }
  const scoreDifference = projection - originalProjection;
  const scoreDifferencePercent = originalProjection > 0 ? scoreDifference / originalProjection : 0;
  
  // More lenient early in game, stricter later
  const threshold = 0.2;
  
  if (scoreDifferencePercent > threshold) {
    return theme.palette.success.main; // Green - performing well
  } else if (scoreDifferencePercent < -threshold) {
    return theme.palette.error.main; // Red - underperforming
  } else {
    return theme.palette.warning.main; // Orange - about as expected
  }
}

// Enhanced player display component with image above name and better layout
const PlayerDisplay = React.memo(({ 
  playerGameScore, 
  playerInfo, 
  leagueId, 
  currentWeekStats, 
  nflGame, 
  currentWeek, 
  awayTeam 
}: {
  playerGameScore: PlayerGameScore;
  playerInfo: Player | undefined;
  leagueId: string | undefined;
  currentWeekStats: any;
  nflGame: NflGame | undefined;
  currentWeek: boolean;
  awayTeam: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [viewingPlayer, setViewingPlayer] = React.useState<Player | undefined>(undefined);
  
  const score = playerGameScore.totalScore ?? 0;
  const completedGame = COMPLETED_GAME_STATUSES.has(nflGame?.status!);
  const rawProjection = completedGame ? playerGameScore.originalProjectedScore : playerGameScore.projectedScore;
  const projection = rawProjection ?? 0;
  
  const gameTimeInfo = getGameTimeInfo(nflGame);
  const isActive = gameTimeInfo?.isLive;
  
  const scoreColor = getScoreColor(playerGameScore.projectedScore!, playerGameScore.originalProjectedScore!, nflGame, theme, currentWeek);
  
  return (
    <>
      <Modal 
        open={viewingPlayer !== undefined} 
        onClose={() => setViewingPlayer(undefined)} 
        aria-labelledby="modal-modal-title" 
        aria-describedby="modal-modal-description"
        sx={{margin: '5%'}}
      >
        <PlayerInfoCard player={viewingPlayer!} leagueId={leagueId}/>
      </Modal>
      
      <Box 
        sx={{
          p: { xs: 0.5, sm: 1.5 },
          borderRadius: 2,
          backgroundColor: isActive ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
          border: isActive ? '1px solid rgba(76, 175, 80, 0.3)' : 'none',
          transition: 'all 0.2s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          minHeight: { xs: '85px', sm: 'auto' },
          justifyContent: 'flex-start',
          maxWidth: '100%'
        }}
      >
        {/* Player Avatar - On Top */}
        {playerInfo && (
          <Avatar 
            alt={playerInfo?.abbr_name} 
            src={playerInfo?.picture}
            sx={{ 
              width: { xs: 28, sm: 36 }, 
              height: { xs: 28, sm: 36 }, 
              mb: 0.5,
              cursor: 'pointer'
            }}
            onClick={() => setViewingPlayer(playerInfo)}
          />
        )}

        {/* Player Name - Truncated, not wrapped */}
        <Box sx={{ 
          width: '100%',
          mb: 0.5,
          cursor: playerInfo ? 'pointer' : 'default'
        }}
        onClick={() => playerInfo && setViewingPlayer(playerInfo)}
        >
          <Typography 
            variant="body2"
            sx={{ 
              fontSize: { xs: '1.0rem', sm: '1.3rem' }, 
              fontWeight: 'bold',
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              lineHeight: 1.2,
              color: 'text.primary'
            }}
          >
            {playerInfo ? playerInfo.abbr_name || `${playerInfo.first_name} ${playerInfo.last_name}` : 'Empty'}
          </Typography>
          
          {/* Position and Team on separate line */}
          <Typography 
            variant="caption"
            sx={{ 
              fontSize: { xs: '0.65rem', sm: '0.75rem' }, 
              color: 'text.secondary',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%'
            }}
          >
            {playerInfo ? `${playerInfo.position} - ${playerInfo.nfl_team?.alias}` : '-'}
          </Typography>
        </Box>

        {/* Matchup Info - Consistent size regardless of game state */}
        <Typography
          variant="caption"
          sx={{ 
            fontSize: { xs: '0.6rem', sm: '0.7rem' }, 
            color: 'text.secondary', 
            mb: 0.5,
            textAlign: 'center',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.2
          }}
        >
          {playerInfo ? getMatchupDisplayText(currentWeekStats, nflGame, playerInfo) : '-'}
        </Typography>
        
        {/* Game Time Indicator */}
        {gameTimeInfo && (
          <Box sx={{ mb: 0.5 }}>
            <Chip
              icon={gameTimeInfo.icon ? <gameTimeInfo.icon sx={{ fontSize: '0.6rem !important' }} /> : undefined}
              label={gameTimeInfo.status}
              size="small"
              sx={{
                height: { xs: 14, sm: 18 },
                fontSize: { xs: '0.5rem', sm: '0.6rem' },
                color: gameTimeInfo.color,
                backgroundColor: `${gameTimeInfo.color}20`,
                border: `1px solid ${gameTimeInfo.color}50`,
                '& .MuiChip-icon': {
                  fontSize: '0.6rem',
                  color: gameTimeInfo.color,
                },
              }}
            />
          </Box>
        )}

        {/* Score Section - Made smaller */}
        <Box sx={{ 
          textAlign: 'center',
          width: '100%'
        }}>
          <ScoreToolTip playerGameScore={playerGameScore}>
            <Box sx={{ cursor: 'pointer' }}>
              {/* Actual Points - Smaller font */}
              <Typography
                sx={{
                  fontSize: { xs: '0.95rem', sm: '1.2rem' },
                  fontWeight: 'bold',
                  color: scoreColor,
                  lineHeight: 1,
                }}
              >
                {(isActive || completedGame) ? score.toFixed(2) : '-'}
              </Typography>
              
              {/* Projected Points - Even smaller */}
              {currentWeek && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { xs: '0.55rem', sm: '0.65rem' },
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    display: 'block',
                    mt: 0.25
                  }}
                >
                  {completedGame ? 'Was:' : 'Proj:'} {projection.toFixed(1)}
                </Typography>
              )}
            </Box>
          </ScoreToolTip>
          
          {/* Progress bar for projection vs actual (current week only) */}
          {currentWeek && !completedGame && score > 0 && isActive && (
            <Box sx={{ 
              mt: 0.5, 
              width: '50px', 
              mx: 'auto'
            }}>
              <LinearProgress
                variant="determinate"
                value={Math.min((score / projection) * 100, 100)}
                sx={{
                  height: 2,
                  width: '50px',
                  borderRadius: 1,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: scoreColor,
                    borderRadius: 1,
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
});

function getPlayerFromRoster(roster: { [key: string]: Array<PlayerGameScore> } | undefined,
  playerMap: Map<string | undefined, Player | undefined>,
  leagueId:string | undefined, position: string, index: number, awayTeam: boolean, nflGameMap: Map<string, NflGame>, currentWeek: boolean) {
  if (roster === null || roster === undefined) {
    return <span>-</span>;
  }
  const playersAtPosition = roster[position];
  if (playersAtPosition === undefined || playersAtPosition[index] === undefined) {
    return emptyPosition;
  }
  const playerId = playersAtPosition[index].playerId;
  const playerInfo = playerMap.get(playerId);
  const currentWeekStats = playerInfo?.quick_stats?.currentWeeksStats;
  const nflGame = currentWeekStats ? nflGameMap.get(playerInfo.nfl_team?.alias!) : undefined;

  return (
    <PlayerDisplay
      playerGameScore={playersAtPosition[index]}
      playerInfo={playerInfo}
      leagueId={leagueId}
      currentWeekStats={currentWeekStats}
      nflGame={nflGame}
      currentWeek={currentWeek}
      awayTeam={awayTeam}
    />
  );
}

export default MatchupStarters;
